import { ZodTypeAny } from "zod";
import {
  RunErrorCode,
  RunErrorResponse,
  runErrorSchema,
  RunResultResponse,
  runResultResponseSchema,
  RunStartResponse,
  runStartResponseSchema,
  RunStatusResponse,
  runStatusResponseSchema,
} from "./runContracts";

type Json =
  | string
  | number
  | boolean
  | null
  | Json[]
  | { [key: string]: Json };

const JSON_HEADERS = {
  "Content-Type": "application/json",
};

function buildUrl(apiBase: string | undefined, path: string) {
  if (apiBase && apiBase.length > 0) {
    return `${apiBase}${path}`;
  }
  return path;
}

export class RunApiError extends Error {
  public readonly code: RunErrorCode;
  public readonly status: number;
  public readonly details?: RunErrorResponse;

  constructor({
    code,
    message,
    status,
    details,
  }: {
    code: RunErrorCode;
    message: string;
    status: number;
    details?: RunErrorResponse;
  }) {
    super(message);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

async function parseJson(res: Response): Promise<Json> {
  const text = await res.text();
  if (!text) {
    return {} as Json;
  }
  try {
    return JSON.parse(text);
  } catch {
    throw new RunApiError({
      code: "runner_failed",
      status: res.status,
      message: "Received unreadable response from server.",
    });
  }
}

async function handleResponse<T>(res: Response, schema: ZodTypeAny): Promise<T> {
  const body = await parseJson(res);
  if (res.ok) {
    try {
      return schema.parse(body) as T;
    } catch {
      throw new RunApiError({
        code: "runner_failed",
        status: res.status,
        message: "Server returned data in an unexpected format.",
      });
    }
  }

  const parsedError = runErrorSchema.safeParse(body);
  if (parsedError.success) {
    throw new RunApiError({
      code: parsedError.data.error,
      status: res.status,
      message:
        parsedError.data.message ||
        ({
          url_required: "Please provide a URL to scan.",
          invalid_url: "Enter a valid URL like https://example.com.",
          home_only: "Only homepage scans are available on the free tier.",
          rate_limited: "You have reached the free-tier limit. Please retry shortly.",
          queue_full: "The runner queue is full right now.",
          not_found: "Run not found.",
          start_failed: "Unable to start run.",
          status_failed: "Unable to fetch status.",
          result_failed: "Unable to fetch run result.",
          runner_failed: "Runner encountered an error.",
          invalid_target: "The supplied URL is not allowed for security reasons.",
        } as Record<RunErrorCode, string>)[parsedError.data.error],
      details: parsedError.data,
    });
  }

  throw new RunApiError({
    code: "runner_failed",
    status: res.status,
    message: "Server returned an unknown error.",
  });
}

export async function startRun({
  apiBase,
  url,
  simulate,
}: {
  apiBase?: string;
  url: string;
  simulate?: boolean;
}): Promise<RunStartResponse> {
  const response = await fetch(buildUrl(apiBase, "/api/run/start"), {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify({ url, simulate: simulate === true }),
  });
  return handleResponse(response, runStartResponseSchema);
}

export async function getRunStatus({
  apiBase,
  runId,
}: {
  apiBase?: string;
  runId: string;
}): Promise<RunStatusResponse> {
  const response = await fetch(buildUrl(apiBase, `/api/run/${runId}/status`));
  return handleResponse(response, runStatusResponseSchema);
}

export async function getRunResult({
  apiBase,
  runId,
}: {
  apiBase?: string;
  runId: string;
}): Promise<RunResultResponse> {
  const response = await fetch(buildUrl(apiBase, `/api/run/${runId}/result`));
  return handleResponse(response, runResultResponseSchema);
}
